import { useContext } from 'react'
import isBoolean from 'lodash/isBoolean'
import isNumber from 'lodash/isNumber'
import useSWR from 'swr'
import { cache, createCacheHelper, useSWRConfig } from 'swr/_internal'
import isTest from '@signal/utils/env/isTest'
import isStorybook from '@signal/utils/env/isStorybook'
import { GenericFetchHookParamsType } from '../contextFetch/types'
import { fetchData, fetchDataNull } from './fetch'
import { FetchHandlerType, FetchHandlerParamsType } from './types'
import { makeCacheKey } from './utils'
import RootContext from './RootContext'

/**
 * due to a bug in SWR 2.0, we must clear any pre-existing error on a cache key before calling useSWR()
 * if using suspense mode or SWR will not issue retries.  This hook takes care of this
 * @param {String} cacheKey - the cache key to clear error state for
 * @param {Boolean} suspenseOverride - optional boolean to override the suspense config
 */
function useClearSWRCacheError(
  cacheKey: string,
  suspenseOverride?: boolean | unknown
): void {
  const swrConfig = useSWRConfig()
  if (isBoolean(suspenseOverride) ? suspenseOverride : swrConfig.suspense) {
    const [getFromSWRCache, setInSWRCache] = createCacheHelper(cache, cacheKey)
    const cachedData = getFromSWRCache()
    // @ts-ignore
    const errorCount = cachedData._pec || 0
    const errorRetryCount = swrConfig.errorRetryCount
      ? swrConfig.errorRetryCount
      : 0
    if (
      cachedData.error &&
      (!isNumber(swrConfig.errorRetryCount) || errorCount < errorRetryCount)
    ) {
      setInSWRCache({
        error: undefined,
        // @ts-ignore
        _pec: errorCount + 1
      })
    }
  }
}

/**
 * @interface UseSWRWithCacheParams
 * this interface defines the set of parameters accepted by the useSWRWithCache hook
 */
interface UseSWRWithCacheParamsType<FetchDataType, BodyDataType>
  extends GenericFetchHookParamsType {
  // the URL to fetch from / post to
  url?: string
  // the JSON body to post / put to the endpoint
  body?: BodyDataType
  // the context for the fetch, for debugging purposes
  context?: string
  // the method to use (default is GET)
  method?: string
  // the fetch handler to use, if not the standard one (fetchData)
  fetchHandler?: FetchHandlerType<FetchDataType>
  // optional options to use with the fetch handler
  fetchOptions?: Record<string, unknown>
  // the fetch timeout to use, if you don't want to use the 30s default
  fetchTimeout?: number
  // response when request is empty (204)
  noContentResponse?: unknown
  // extra options for useSWR, if any
  swrOptions?: Record<string, unknown>
  // optional function to transform the resulting data
  transformFunc?: (data: unknown) => FetchDataType
  // if true, add a cache busting param to the url (due to weird issues like MRD-1517)
  cacheBust?: boolean
  /* webpack-strip-block:removed */
}

/**
 * this is a wrapper hook around useSWR designed to be used for cacheable data fetching.  SWR has a cache but does
 * not use it to short-circuit requests, only for request de-duping.
 * @param {UseSWRWithCacheParams} - the parameters as defined in the interface above
 * @return {FetchDataType | undefined} - SWR returns undefined unless the data has been fetched,
 *   in which case that data is returned
 */
export function useSWRWithCache<FetchDataType, BodyDataType = FetchDataType>({
  url = '',
  body,
  context,
  method,
  fetchHandler = fetchData,
  fetchTimeout,
  fetchOptions = {},
  noContentResponse = {},
  swrOptions,
  transformFunc,
  /* webpack-strip-block:removed */
  cacheBust
}: UseSWRWithCacheParamsType<FetchDataType, BodyDataType>):
  | FetchDataType
  | undefined {
  const { fetcher = window.fetch, useCredentials } = useContext(RootContext)
  const serializedBody = body && JSON.stringify(body)
  // TODO: remove the isTest() check once we have updated test with mockData & useMockData
  // Debt case will be created to remove this check (FIA-118)
  const cacheKey =
    makeCacheKey(url, serializedBody) + (!isTest() ? context : '')

  if (url === '') {
    fetchHandler = fetchDataNull(
      /* webpack-strip-block:removed */
    )
  }

  useClearSWRCacheError(cacheKey)

  const { data, error } = useSWR(
    cacheKey,
    async (): Promise<FetchDataType | undefined> => {
      /* webpack-strip-block:removed */

      const [getFromSWRCache] = createCacheHelper(cache, cacheKey)
      const cachedData = getFromSWRCache().data
      if (!cachedData) {
        console.log('[swr] useSWRWithCache not cached', context, url, body)
        if (
          !context?.includes('mock') &&
          (isTest() || (!context?.includes('storybook') && isStorybook()))
        ) {
          return undefined
        }
      } else if (!cacheBust) {
        return cachedData
      }

      if (cacheBust) {
        url += `${url.includes('?') ? '&' : '?'}cache_bust=${Date.now()}`
      }

      const ret = await fetchHandler({
        url,
        fetcher,
        fetchTimeout,
        options: {
          body: serializedBody,
          method,
          ...fetchOptions
        },
        useCredentials,
        noContentResponse
      })
      return transformFunc ? transformFunc(ret) : ret
    },
    swrOptions
  )
  if (error) {
    throw error
  }
  return data
}

/**
 * this is a wrapper function around SWR designed to be used for cacheable POSTs for data fetching
 * @param {String} url - the URL to POST to
 * @param {Object} body - POST data
 * @param {String} context - the context of this POST, for debugging purposes
 * @param {Function} transformFunc - an optional function to transform the resulting data
 * @param {Object} swrOptions - options that can be passed to SWR (such as { suspense: false })
 * @param {Number} fetchTimeout - the fetch timeout to use, if you don't want to use the 90sec default
 * @return {Object} - the data from the SWR response object
 */
export function usePost<FetchDataType>(
  url: string,
  body: FetchDataType,
  context: string,
  transformFunc?: (data: unknown) => FetchDataType,
  swrOptions?: Record<string, unknown>,
  fetchTimeout?: number
): FetchDataType | undefined {
  return useSWRWithCache<FetchDataType>({
    url,
    body,
    context,
    method: 'POST',
    transformFunc,
    swrOptions,
    fetchTimeout
  })
}

/**
 * this is a wrapper function around SWR designed to be used for cacheable GETs for data fetching
 * @param {String} urlOrCacheKey - the URL or cache key to GET
 * @param {String} context - the context of this GET, for debugging purposes
 * @param {Function} fetcher - a fetcher function to fetch data that returns a Promise, as supported by useSWR
 * @param {Function} transformFunc - an optional function to transform the resulting data
 * @param {Object} swrOptions - options that can be passed to SWR (such as { suspense: false })
 * @param {Number} fetchTimeout - the fetch timeout to use, if you don't want to use the 90sec default
 * @return {Object} - the data from the SWR response object
 */
export function useGet<FetchDataType>(
  urlOrCacheKey: string,
  context: string,
  fetchHandler?: (params: FetchHandlerParamsType) => Promise<FetchDataType>,
  transformFunc?: (data: unknown) => FetchDataType,
  swrOptions?: Record<string, unknown>,
  fetchOptions?: Record<string, unknown>,
  fetchTimeout?: number
): FetchDataType | undefined {
  return useSWRWithCache<FetchDataType>({
    url: urlOrCacheKey,
    context,
    fetchHandler,
    fetchOptions,
    fetchTimeout,
    method: 'GET',
    transformFunc,
    swrOptions
  })
}

export default useSWRWithCache
